/**
 * The story-merge flatten, derived (spec decision 3). Projects a committed
 * CONVERGENCE#PROFILE onto the character profile as `profile.converged` so
 * every sheet-math helper (combinedLevel / proficiencyBonus / totalHp / spell
 * slots) treats the character as ONE class at the merged level. Nothing is
 * written back — sessionApi.updateCharacterProfile strips the overlay again —
 * and an un-converged character is returned untouched. Mirrors
 * ConvergedOverlayLoader on the C# side.
 *
 * Pure (no auth / network imports) so it is unit-testable; sessionApi applies
 * it to every roster read.
 */
import type { AbilityScore, ConvergedOverlay } from './character';
import type { ConvergenceProfile } from './convergence';

/** The minimum bundle shape the overlay needs — structurally satisfied by CharacterBundle. */
export interface OverlayableBundle {
  profile: { converged?: ConvergedOverlay } | null;
  convergence?: { profile: ConvergenceProfile | null } | null;
}

export function overlayFromConvergence(profile: ConvergenceProfile | null | undefined): ConvergedOverlay | null {
  if (!profile || !profile.name || !(profile.level > 0)) return null;
  const l = profile.leanings;
  return {
    name: profile.name,
    level: profile.level,
    hitDie: l?.hitDie || undefined,
    hitDieGained: profile.hitDieGained || undefined,
    saves: l?.saves?.length ? (l.saves as AbilityScore[]) : undefined,
    primaryAbility: l?.primaryAbility,
    casterFraction: l?.casterFraction,
  };
}

export function applyConvergedOverlay<T extends OverlayableBundle>(bundle: T): T {
  const overlay = overlayFromConvergence(bundle.convergence?.profile);
  if (!bundle.profile) return bundle;
  if (!overlay) {
    if (bundle.profile.converged) {
      const { converged: _dropped, ...rest } = bundle.profile;
      return { ...bundle, profile: rest };
    }
    return bundle;
  }
  return { ...bundle, profile: { ...bundle.profile, converged: overlay } };
}
